import React, { useCallback, useMemo, useState } from 'react'
import {
  Button,
  Flex,
  Heading,
  InjectedModalProps,
  LinkExternal,
  ModalBody,
  ModalCloseButton,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  Progress,
  Skeleton,
  Text,
  WarningIcon,
} from '@hunnyswap/uikit'
import styled from 'styled-components'
import { useTradingRewardsInfo } from 'hooks/useTradingRewardsInfo'
import BigNumber from 'bignumber.js'
import useClaimReward from '../hooks/useClaimReward'
import CircleLoader from 'components/Loader/CircleLoader'

const StyledModalContainer = styled(ModalContainer)`
  max-width: 620px;
  width: 100%;
`

const StyledProgressContainer = styled.div`
  position: relative;
`

const Divider = styled.div`
  background-color: ${({ theme }) => theme.colors.textSubtle};
  height: 1px;
  margin: 24px 0;
  width: 100%;
`

const fullyClaimableStepInDay = 7

interface ClaimTradingRewardsModalProps extends InjectedModalProps {}

const ClaimTradingRewardsModal: React.FC<ClaimTradingRewardsModalProps> = ({ onDismiss = () => null }) => {
  const { claimPercent, rewardsBalance, isLoading, resetData } = useTradingRewardsInfo()
  const progress = claimPercent ? (claimPercent - 0.02) / 0.98 : claimPercent
  const [isClaiming, setIsClaiming] = useState(false)

  const displayCountDown = useMemo(() => {
    const daysLeft = fullyClaimableStepInDay - fullyClaimableStepInDay * progress
    const hoursLeft = 24 * (daysLeft % 1)
    if (daysLeft === 0 && hoursLeft === 0) {
      return 'Fully claim'
    }
    return `${Math.floor(daysLeft) ? `${Math.floor(daysLeft)} ${daysLeft >= 2 ? 'days' : 'day'}` : ''} ${
      Math.floor(hoursLeft) ? `${Math.floor(hoursLeft)} ${hoursLeft >= 2 ? 'hours' : 'hour'}` : ''
    } left to fully claim`
  }, [progress])

  const displayAccureDays = useMemo(() => {
    const daysLeft = fullyClaimableStepInDay * progress
    const hoursLeft = 24 * (daysLeft % 1)

    return `${Math.floor(daysLeft) ? `${Math.floor(daysLeft)} ${daysLeft >= 2 ? 'days' : 'day'}` : ''} ${
      Math.floor(hoursLeft) ? `${Math.floor(hoursLeft)} ${hoursLeft >= 2 ? 'hours' : 'hour'}` : ''
    }`
  }, [progress])

  const { onClaim } = useClaimReward()

  const handleClaimReward = useCallback(async () => {
    setIsClaiming(true)
    onClaim(rewardsBalance.multipliedBy(claimPercent).toFixed(3, BigNumber.ROUND_DOWN))
      .then(async (response) => {
        if (!response) {
          setIsClaiming(false)
          return
        }

        await response.wait()
        setIsClaiming(false)
        resetData()
      })
      .catch((err) => {
        setIsClaiming(false)
        console.error(err)
      })
  }, [onClaim])

  return (
    <StyledModalContainer minWidth="320px">
      <ModalHeader>
        <ModalTitle>
          <Heading>Claim rewards</Heading>
        </ModalTitle>
        <ModalCloseButton onDismiss={onDismiss} />
      </ModalHeader>
      <ModalBody p="24px">
        <Flex justifyContent="space-between">
          <Text>Your trading rewards</Text>
          <Text bold>
            {isLoading ? (
              <Skeleton width="102px" height="24px" />
            ) : (
              <Text bold>{rewardsBalance.toFixed(3, BigNumber.ROUND_DOWN)} XOXO</Text>
            )}
          </Text>
        </Flex>
        <Flex justifyContent="space-between" mt="24px">
          <Text fontSize="14px">0 </Text>
          <Text fontSize="14px">{Math.round(fullyClaimableStepInDay)} days</Text>
        </Flex>
        <StyledProgressContainer>
          <Progress scale="lg" primaryStep={progress * 100} />
          <Text fontSize="12px" style={{ position: 'absolute', bottom: 3, left: 12 }}>
            {displayCountDown}
          </Text>
        </StyledProgressContainer>
        <Flex justifyContent="flex-end">
          <Text fontSize="14px">Your accure: {displayAccureDays.trim() ? displayAccureDays : '0 day'}</Text>
        </Flex>
        <Flex justifyContent="space-between" mt="24px">
          <Flex justifyContent="space-between">
            <WarningIcon color="failure" width="24px" />
            <Text color="failure" ml="4px">
              Early claim fee
            </Text>
          </Flex>

          {isLoading ? (
            <Skeleton width="102px" height="24px" />
          ) : (
            <Text color="failure" bold>
              {rewardsBalance.minus(rewardsBalance.multipliedBy(claimPercent)).toFixed(3, BigNumber.ROUND_DOWN)} XOXO
            </Text>
          )}
        </Flex>
        <Text color="textSubtle" fontSize="14px" mt="12px">
          You should wait for 7 more days to claim your trading rewards without fees. {/* TODO update docs link */}
        </Text>

        <Divider />

        <Flex justifyContent="space-between">
          <Text bold>Your will receive</Text>
          {isLoading ? (
            <Skeleton width="102px" height="24px" />
          ) : (
            <Text bold>{rewardsBalance.multipliedBy(claimPercent).toFixed(3, BigNumber.ROUND_DOWN)} XOXO</Text>
          )}
        </Flex>

        <Button mt="36px" onClick={handleClaimReward} disabled={isClaiming}>
          {isClaiming ? <>Cliaming</> : <>Claim</>}
        </Button>
      </ModalBody>
    </StyledModalContainer>
  )
}

export default ClaimTradingRewardsModal
